import { Link, NavLink } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Menu, X, Phone, ChevronDown, ArrowRight } from 'lucide-react';
import { NAV, CONTACT_INFO } from '@/lib/content';
import Logo from '@/components/Logo';
import { SERVICE_CATEGORIES, getServicesByCategory } from '@/lib/services';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesHover, setServicesHover] = useState(false);
  const closeTimer = useRef(null);

  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesHover(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesHover(false), 220);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md border-b border-bp-slate-200' : 'bg-white border-b border-transparent'
      }`}
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 h-[78px] flex items-center justify-between">
        <Logo size="md" />

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV.map((n) => {
            if (n.label === 'Services') {
              return (
                <div
                  key={n.to}
                  className="relative"
                  onMouseEnter={openMega}
                  onMouseLeave={scheduleClose}
                >
                  <NavLink
                    to={n.to}
                    data-testid="nav-link-services"
                    onClick={() => setServicesHover(false)}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 px-4 py-2 text-[14.5px] font-medium rounded-full transition-colors ${
                        isActive ? 'text-bp-blue bg-bp-blue-50' : 'text-bp-slate hover:text-bp-blue'
                      }`
                    }
                  >
                    {n.label}
                    <ChevronDown size={14} className={`transition-transform ${servicesHover ? 'rotate-180' : ''}`}/>
                  </NavLink>

                  {/* MEGA DROPDOWN */}
                  {servicesHover && (
                    <div
                      data-testid="services-mega-menu"
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[920px]"
                      onMouseEnter={openMega}
                      onMouseLeave={scheduleClose}
                    >
                      <div className="bg-white border border-bp-slate-200 rounded-2xl shadow-2xl overflow-hidden">
                        <div className="grid grid-cols-4 gap-0">
                          {SERVICE_CATEGORIES.map((cat) => (
                            <div key={cat.key} className="p-5 border-r border-bp-slate-100 last:border-r-0">
                              <div className="text-[11px] font-bold uppercase tracking-wider text-bp-blue mb-3">{cat.title}</div>
                              <ul className="space-y-2.5">
                                {getServicesByCategory(cat.key).map((s) => (
                                  <li key={s.slug}>
                                    <Link
                                      to={`/services/${s.slug}`}
                                      onClick={() => setServicesHover(false)}
                                      data-testid={`mega-link-${s.slug}`}
                                      className="block text-[13.5px] text-bp-slate hover:text-bp-blue leading-snug transition-colors"
                                    >
                                      {s.name.replace(/\s*\([^)]*\)/g, '')}
                                    </Link>
                                  </li>
                                ))}
                                {cat.key === 'growth' && (
                                  <li>
                                    <Link
                                      to="/vsm"
                                      onClick={() => setServicesHover(false)}
                                      data-testid="mega-link-vsm"
                                      className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-bp-blue hover:underline leading-snug transition-colors"
                                    >
                                      Virtual Sales Management
                                      <span className="text-[9px] font-bold uppercase tracking-widest bg-bp-teal/15 text-bp-teal rounded-full px-1.5 py-0.5">New</span>
                                    </Link>
                                  </li>
                                )}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <div className="bg-bp-slate-50 border-t border-bp-slate-100 px-5 py-3 flex items-center justify-between">
                          <span className="text-[13px] text-bp-slate-soft">24 services · all in-house · 19 years</span>
                          <Link
                            to="/services"
                            onClick={() => setServicesHover(false)}
                            className="inline-flex items-center gap-1.5 text-[13px] font-bold text-bp-blue hover:underline"
                          >
                            View all services <ArrowRight size={13}/>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 text-[14.5px] font-medium rounded-full transition-colors ${
                    isActive ? 'text-bp-blue bg-bp-blue-50' : 'text-bp-slate hover:text-bp-blue'
                  }`
                }
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={`tel:${CONTACT_INFO.phoneTel}`} className="text-[13px] font-semibold text-bp-slate hover:text-bp-blue inline-flex items-center gap-1.5">
            <Phone size={14} /> {CONTACT_INFO.phone}
          </a>
          <Link to="/contact" data-testid="nav-cta" className="btn-primary py-2.5 px-5 text-[14px]">
            Book a Strategy Call
          </Link>
        </div>

        <button
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          className="lg:hidden inline-flex w-11 h-11 items-center justify-center text-bp-ink rounded-full hover:bg-bp-slate-100"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* MOBILE PANEL */}
      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden fixed inset-0 top-[78px] z-30 bg-white px-5 pt-6 pb-12 overflow-y-auto">
          <ul className="space-y-1">
            {NAV.map((n) => {
              if (n.label === 'Services') {
                return (
                  <li key={n.to}>
                    <button
                      type="button"
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      data-testid="mobile-services-toggle"
                      className="w-full flex items-center justify-between py-4 border-b border-bp-slate-200 text-2xl font-semibold text-bp-ink"
                    >
                      Services
                      <ChevronDown size={20} className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}/>
                    </button>
                    {mobileServicesOpen && (
                      <div className="py-3 space-y-4">
                        {SERVICE_CATEGORIES.map((cat) => (
                          <div key={cat.key}>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-bp-blue mb-2">{cat.title}</div>
                            <ul className="space-y-1">
                              {getServicesByCategory(cat.key).map((s) => (
                                <li key={s.slug}>
                                  <Link
                                    to={`/services/${s.slug}`}
                                    onClick={() => { setOpen(false); setMobileServicesOpen(false); }}
                                    className="block py-1.5 text-[14px] text-bp-slate hover:text-bp-blue"
                                  >
                                    {s.name.replace(/\s*\([^)]*\)/g, '')}
                                  </Link>
                                </li>
                              ))}
                              {cat.key === 'growth' && (
                                <li>
                                  <Link
                                    to="/vsm"
                                    onClick={() => { setOpen(false); setMobileServicesOpen(false); }}
                                    className="inline-flex items-center gap-1.5 py-1.5 text-[14px] font-semibold text-bp-blue"
                                  >
                                    Virtual Sales Management
                                    <span className="text-[9px] font-bold uppercase tracking-widest bg-bp-teal/15 text-bp-teal rounded-full px-1.5 py-0.5">New</span>
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        ))}
                        <Link
                          to="/services"
                          onClick={() => { setOpen(false); setMobileServicesOpen(false); }}
                          className="inline-flex items-center gap-1.5 mt-2 text-[14px] font-bold text-bp-blue"
                        >
                          View all services <ArrowRight size={13}/>
                        </Link>
                      </div>
                    )}
                  </li>
                );
              }
              return (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.to === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-4 border-b border-bp-slate-200 text-2xl font-semibold ${
                        isActive ? 'text-bp-blue' : 'text-bp-ink'
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
          <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary mt-8 w-full" data-testid="nav-mobile-cta">
            Book a Strategy Call
          </Link>
          <a href={`tel:${CONTACT_INFO.phoneTel}`} className="mt-3 btn-secondary w-full"><Phone size={15}/> {CONTACT_INFO.phone}</a>
        </div>
      )}
    </header>
  );
}
